import { useEffect } from 'react';
import { motion } from 'motion/react';
import { cva } from 'class-variance-authority';
import styles from './workspace-view.module.css';

const contentWrapper = cva(styles.contentWrapper);
const editorWrapper = cva(styles.editorWrapper);
import { useUIStore } from '@/stores/use-ui-store';
import { useSessionState } from '@/session';
import { HomePage } from '@/app/routes/home';
import { DocumentLibrary } from '@/components/document-cards/document-library';
import { CollectionLibrary } from '@/features/collections/components/collection-library';
import { TrashLibrary } from '@/components/document-cards/trash-library';
import { DocumentEditor } from '@/features/editor/components/document-editor';
import { DiffViewContainer } from '@/app/routes/workspace/diff-view';
import { CollectionDetail } from '@/features/collections/components/collection-detail';
import { StatusBar } from '@/layout/status-bar';
import { EditorErrorBoundary } from '@/features/editor/components/editor-error-boundary';
import { CalendarPage } from '@/features/calendar/components/calendar-page';
import { Flex, useReducedMotion, LogoAnimated } from '@nula/core-ui';
import { usePermission } from '@/lib/sharing/components/permission-gate';
import { CanvasEditor } from '@/features/canvas/components/shell/canvas-editor';
import { SpaceMount } from '@/features/canvas/components/shell/space-mount';
import { ConversationTab } from './conversation-tab';
import { useTrackFocusedConversation } from './use-track-focused-conversation';

export interface WorkspaceViewProps {
  panelId: string;
  activeId: string;
  isFocused: boolean;
}

function CanvasContent({ entityId }: { entityId: string }) {
  const { loading, readOnly } = usePermission(entityId);

  if (loading) {
    return (
      <Flex vertical align="center" justify="center" className={styles.canvasLoader}>
        <LogoAnimated loop duration={2400} holdDuration={600} className={styles.loaderLogo} />
      </Flex>
    );
  }

  return <CanvasEditor entityId={entityId} readOnly={readOnly} />;
}

/**
 * EditorWrapper — isolates editorRevision subscription so that revision bumps
 * only re-render the error boundary, not the entire WorkspaceView.
 *
 * Recording lifecycle UI lives on the document's surfaces now (status banner,
 * controls, transcript surface) — the legacy per-document RecordingBanner here
 * was redundant (and wasn't displaying), so it's been removed.
 */
function EditorWrapper({ documentId, isMain }: { documentId: string; isMain: boolean }) {
  const editorRevision = useUIStore((s) => s.editorRevision);
  return (
    <>
      <Flex vertical className={editorWrapper()}>
        <EditorErrorBoundary key={`${documentId}-${isMain ? editorRevision : 0}`}>
          <DocumentEditor documentId={documentId} />
        </EditorErrorBoundary>
      </Flex>
      <StatusBar />
    </>
  );
}

/**
 * WorkspaceView — routes a panel's activeId to the appropriate view component.
 *
 * Named views ('home', 'documents', 'collections', 'trash') render their
 * respective library/home components. The tab type (from TabEntry) determines
 * whether to render CollectionDetail, CanvasEditor, SpaceMount, or DocumentEditor.
 * DiffView overlays the focused panel when useUIStore.diffView is set.
 */
export function WorkspaceView({ panelId, activeId, isFocused }: WorkspaceViewProps) {
  const diffView = useUIStore((s) => s.diffView);
  const setDiffView = useUIStore((s) => s.setDiffView);
  const shouldReduce = useReducedMotion();
  const editorRevision = useUIStore((s) => s.editorRevision);

  useEffect(() => {
    if (isFocused && diffView && diffView.entityId !== activeId) {
      setDiffView(null);
    }
  }, [activeId, isFocused, diffView, setDiffView]);
  const { state } = useSessionState();

  // Read the tab type directly from the panel's tab list — no projection lookups
  const panel = state?.panels[panelId];
  const activeTab = panel?.tabs.find((t) => t.id === activeId);
  const tabType = activeTab?.type ?? 'document';

  // Track which conversation (if any) is focused in the workspace. Only the
  // focused panel writes; non-focused panels are no-ops so split-view doesn't
  // clobber the signal. Badge gates read focusedConversationId, not
  // currentConversationId, to avoid the stale-continuity-pointer bug.
  useTrackFocusedConversation({ isFocused, activeId, tabType });

  // Named views
  if (tabType === 'view') {
    if (activeId === 'home') return <HomePage />;
    if (activeId === 'documents') return <DocumentLibrary />;
    if (activeId === 'collections') return <CollectionLibrary />;
    if (activeId === 'trash') return <TrashLibrary />;
    if (activeId === 'calendar') return <CalendarPage />;
  }

  // Collection
  if (tabType === 'collection') {
    return (
      <motion.div
        key={activeId}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={shouldReduce ? { duration: 0 } : { duration: 0.25, ease: 'easeOut' }}
        className={contentWrapper()}
      >
        <CollectionDetail collectionId={activeId} isBackground={!isFocused} />
      </motion.div>
    );
  }

  // DiffView overlays focused panel only
  if (diffView && isFocused) {
    return (
      <motion.div
        key={`diff-${activeId}`}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={shouldReduce ? { duration: 0 } : { duration: 0.25, ease: 'easeOut' }}
        className={contentWrapper()}
      >
        <DiffViewContainer />
      </motion.div>
    );
  }

  // Canvas — entity kind='canvas', backed by canvases.state BYTEA
  if (tabType === 'canvas') {
    return <CanvasContent key={`${activeId}-${editorRevision}`} entityId={activeId} />;
  }

  // Space — entity kind='space', canvas backed by spaces.canvas_state BYTEA
  if (tabType === 'space') {
    return <SpaceMount entityId={activeId} />;
  }

  // Saved conversation — its own tab with its own Chat instance.
  // key={activeId} forces unmount/remount when switching between conversation
  // tabs, so AssistantChatProvider's useState initializer re-runs with the new
  // chatId. Without the key, React reconciles the same instance and only
  // updates the prop — the Chat stays bound to the first conversation.
  // Consistent with canvas (key={`${activeId}-${editorRevision}`}) and
  // document (key={activeId} on the motion.div wrapper).
  if (tabType === 'conversation') {
    return <ConversationTab key={activeId} conversationId={activeId} panelId={panelId} />;
  }

  // Default: document editor (also fallback for unknown/unloaded tabType)
  return (
    <motion.div
      key={activeId}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={shouldReduce ? { duration: 0 } : { duration: 0.25, ease: 'easeOut' }}
      className={contentWrapper()}
    >
      <EditorWrapper documentId={activeId} isMain={isFocused} />
    </motion.div>
  );
}
